import {connect} from 'react-redux';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {MessagePatch} from '../../../../helpers/models/messagePatchHelpers';
import {VisualRecipientMap} from '../../../../helpers/models/recipientHelpers';
import {findSignature} from '../../../../helpers/models/signatureHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {SignatureModel} from '../../../../models/api/signatureModel';
import {AppState} from '../../../../store/appState';
import {makeGetAvailableSignatures} from '../../../../store/data/signatures/signaturesSelectors';

/*
 * Props.
 */

export interface SignaturesEnhancerProps {
  channel: ChannelModel;
  message: DraftMessageModel;
  recipients: VisualRecipientMap;
  saveMessageAsync: HandlerOf<MessagePatch>;
}

interface SignaturesEnhancerStateProps {
  availableSignatures: ReadonlyArray<SignatureModel>;
}

export interface SignaturesEnhancerInjectedProps extends SignaturesEnhancerStateProps {
  editSignature: Handler;
  revertSignature: Handler;
  addSignature: Handler;
  removeSignature: Handler;
  replaceSignature: HandlerOf<number>;
}

type SignaturesEnhancerMergedProps = SignaturesEnhancerProps & SignaturesEnhancerInjectedProps;

/*
 * Selectors.
 */

function getChannel(_state: AppState, props: SignaturesEnhancerProps) {
  return props.channel;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SignaturesEnhancerProps>()(() => ({
  availableSignatures: makeGetAvailableSignatures(getChannel),
}));

export const withSignatures = connect<
  SignaturesEnhancerStateProps,
  {},
  SignaturesEnhancerProps,
  SignaturesEnhancerMergedProps,
  AppState
>(mapStateToProps, null, (stateProps, _dispatchProps, ownProps) => {
  const {availableSignatures} = stateProps;
  const {message, saveMessageAsync} = ownProps;

  const editSignature = () => saveMessageAsync({newWasSignatureModified: true});
  const revertSignature = () => {
    const signature = findSignature(message.signatureId, availableSignatures);
    if (!signature) {
      return;
    }

    saveMessageAsync({
      newSignatureId: signature.id,
      newSignatureHtml: undefined,
      newWasSignatureModified: false,
    });
  };

  const addSignature = () => {
    const signature = availableSignatures[0];
    if (!signature) {
      return;
    }

    replaceSignature(signature.id);
  };

  const removeSignature = () => saveMessageAsync({newIsSignatureIncluded: false});

  const replaceSignature: HandlerOf<number> = (newSignatureId) => {
    const signature = findSignature(newSignatureId, availableSignatures);
    if (!signature) {
      return;
    }

    saveMessageAsync({
      newSignatureId: signature.id,
      newSignatureHtml: undefined,
      newIsSignatureIncluded: true,
      newWasSignatureModified: false,
    });
  };

  return {
    ...ownProps,
    availableSignatures,
    editSignature,
    revertSignature,
    addSignature,
    removeSignature,
    replaceSignature,
  };
});
